import { useEffect, useRef, useState } from 'react';
import { Modal, notification } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import Toast from 'components/shared/Alert';
import { useLanguage } from 'hooks/Language/useLanguage';
import useAccountAccess from 'hooks/useAccountAccess';
import useKycDocuments from 'hooks/useKycDocuments';
import { useLocation } from 'react-router-dom';
import urls from 'routes/urls';
import {
  onOpenKycPrompt,
  publishKycProgress,
} from 'utils/kycProgressStore';
import useKycUpload, { MAX_KYC_FILE_MB } from 'hooks/useKycUpload';
import { getUserData } from 'queries/ossix/user/userData';
import {
  dismissKycPrompt,
  IKycDocumentState,
  isAccountVerified,
  firstMissing,
  isKycPromptDismissed,
} from 'utils/kycStatus';
import KycDocumentStep from './KycDocumentStep';
import KycDone from './KycDone';
import KycReminder from './KycReminder';
import KycStepRail from './KycStepRail';
import './index.scss';

const KycGateInner: React.FC = () => {
  const { translate } = useLanguage();
  const [api, contextHolder] = notification.useNotification();
  const { userData, refetch } = getUserData();

  const userProfile = userData && userData?.[0];
  const {
    documents,
    isChecking,
    isComplete,
    done,
    total,
    markUploaded,
  } = useKycDocuments(userProfile);
  // Only changes the wording of the last screen: a verified account can
  // still be missing documents, so it never decides whether we prompt.
  const verified = isAccountVerified(userProfile);
  const { pathname } = useLocation();
  const { isPending: isAccountPending } = useAccountAccess();

  const [open, setOpen] = useState(false);
  const [finished, setFinished] = useState(false);
  const [activeKey, setActiveKey] = useState(documents[0].key);
  const [stamp] = useState(() => Date.now());
  const autoOpened = useRef(false);

  // Decide once per page load, as soon as the documents are known.
  useEffect(() => {
    if (!userProfile || isChecking || autoOpened.current) return;
    autoOpened.current = true;
    setActiveKey((firstMissing(documents) || documents[0]).key);
    if (!isComplete && !isKycPromptDismissed()) setOpen(true);
  }, [userProfile, isChecking]);

  const {
    drafts,
    statuses,
    errors,
    uploadedPreviews,
    onPick,
    onDiscard,
    onConfirm,
  } = useKycUpload({
    onInvalid: (reason) =>
      Toast(
        api,
        'Error',
        reason === 'type'
          ? translate('Please choose a PNG or JPG image.')
          : `${translate('File is too large. Max size:')} ${MAX_KYC_FILE_MB}MB`,
      ),
    onUploaded: (doc) => {
      markUploaded(doc.key);
      refetch();
      const next = firstMissing(documents, doc.key);
      if (next) setActiveKey(next.key);
      else setFinished(true);
    },
  });

  const previewFor = (doc: IKycDocumentState) =>
    uploadedPreviews[doc.key] ||
    (doc.presence === 'present' && doc.url
      ? `${doc.url}?v=${stamp}`
      : undefined);

  // Share progress with the dashboard card and let it open this modal.
  useEffect(() => {
    publishKycProgress({
      known: !isChecking,
      done,
      total,
      isComplete,
      documents: documents.map((doc) => ({
        key: doc.key,
        label: doc.label,
        present: doc.presence === 'present',
        preview: previewFor(doc),
      })),
    });
    // The store ignores identical snapshots, so running after every render
    // only notifies subscribers when something actually changed.
  });

  useEffect(
    () =>
      onOpenKycPrompt(() => {
        setActiveKey((firstMissing(documents) || documents[0]).key);
        setOpen(true);
      }),
    [documents],
  );

  const onLater = () => {
    dismissKycPrompt();
    setOpen(false);
  };

  const onFinish = () => {
    setOpen(false);
    setFinished(false);
  };

  if (!userProfile) return <>{contextHolder}</>;

  const activeIndex = documents.findIndex(
    (doc) => doc.key === activeKey,
  );
  const active = documents[activeIndex] || documents[0];
  // On the dashboard of an account still being opened, the status card is
  // the call to action; the floating reminder would only duplicate it.
  const statusCardShown =
    isAccountPending && pathname === urls.dashboard;
  const showReminder =
    !open && !isChecking && !isComplete && !statusCardShown;

  return (
    <>
      {contextHolder}
      {showReminder && (
        <KycReminder
          done={done}
          total={total}
          onOpen={() => setOpen(true)}
        />
      )}

      <Modal
        open={open}
        onCancel={finished ? onFinish : onLater}
        footer={null}
        width={920}
        centered
        className="kyc-gate-modal"
        maskClosable={false}
      >
        {finished ? (
          <KycDone
            documents={documents}
            previews={Object.fromEntries(
              documents.map((doc) => [doc.key, previewFor(doc)]),
            )}
            verified={verified}
            onClose={onFinish}
          />
        ) : (
          <div className="kyc-wizard">
            <KycStepRail
              documents={documents}
              activeKey={active.key}
              statuses={statuses}
              done={done}
              total={total}
              onSelect={setActiveKey}
              onLater={onLater}
            />

            <section className="kyc-wizard__main">
              <KycDocumentStep
                key={active.key}
                doc={active}
                draftUrl={drafts[active.key]?.url}
                savedUrl={previewFor(active)}
                status={statuses[active.key] || 'idle'}
                errorMessage={errors[active.key]}
                onPick={(file) => onPick(active, file)}
                onConfirm={() => onConfirm(active)}
                onDiscard={() => onDiscard(active)}
              />

              <nav className="kyc-wizard__nav">
                <button
                  type="button"
                  disabled={activeIndex <= 0}
                  onClick={() =>
                    setActiveKey(documents[activeIndex - 1].key)
                  }
                >
                  <LeftOutlined /> {translate('Previous')}
                </button>
                <span>
                  {translate('Step')} {activeIndex + 1} /{' '}
                  {documents.length}
                </span>
                {activeIndex < documents.length - 1 ? (
                  <button
                    type="button"
                    onClick={() =>
                      setActiveKey(documents[activeIndex + 1].key)
                    }
                  >
                    {translate('Next')} <RightOutlined />
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={!isComplete}
                    onClick={() => setFinished(true)}
                  >
                    {translate('Finish')} <RightOutlined />
                  </button>
                )}
              </nav>
            </section>
          </div>
        )}
      </Modal>
    </>
  );
};

/**
 * ManageRoutes also renders the public auth pages, so the inner component
 * (and its GetUserData call) is only mounted for a signed-in user.
 */
const KycGate: React.FC = () => {
  const token = localStorage.getItem('userToken');
  const userPID = localStorage.getItem('userPID');
  if (!token || !userPID) return null;
  return <KycGateInner />;
};

export default KycGate;
